import React, { type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode, useEffect, useRef, useState, memo } from 'react';
import {
  ArrowRight,
  Check,
  CheckCircle2,
  ChevronRight,
  Menu,
  Receipt,
  Search,
  ShieldCheck,
  Smartphone,
  Store,
  X,
} from 'lucide-react';
import Logo, { AstraMarkOnly, PlaidMarkOnly } from '../Logo';
import { ANDROID_INVITE_URL, IOS_INVITE_URL } from '../../constants/downloadLinks';

const cx = (...parts: Array<string | false | null | undefined>) => parts.filter(Boolean).join(' ');

type ButtonLikeProps = {
  children: ReactNode;
  className?: string;
  href?: string;
  onClick?: () => void;
  target?: AnchorHTMLAttributes<HTMLAnchorElement>['target'];
  rel?: AnchorHTMLAttributes<HTMLAnchorElement>['rel'];
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  disabled?: boolean;
  ariaLabel?: string;
};

const buttonBaseClassName =
  'inline-flex items-center justify-center gap-3 rounded-[var(--clero-radius-xl)] px-5 py-3.5 text-sm font-semibold tracking-[-0.01em] transition duration-200 disabled:cursor-not-allowed disabled:opacity-60';

const ButtonLike: React.FC<ButtonLikeProps & { tone: 'primary' | 'secondary' | 'ghost' }> = ({
  ariaLabel,
  children,
  className,
  disabled,
  href,
  onClick,
  rel,
  target,
  tone,
  type = 'button',
}) => {
  const toneClassName = {
    primary: 'bg-[var(--clero-slate-950)] text-white shadow-[var(--clero-shadow-card)] hover:-translate-y-0.5 hover:bg-[#111c2a]',
    secondary: 'bg-[var(--clero-primary)] text-white shadow-[var(--clero-shadow-teal)] hover:-translate-y-0.5 hover:bg-[var(--clero-primary-dark)]',
    ghost: 'border border-slate-200/80 bg-white/72 text-slate-800 shadow-[0_10px_30px_rgba(15,23,42,0.05)] hover:border-slate-300/90 hover:bg-white',
  }[tone];

  if (href) {
    return (
      <a
        aria-label={ariaLabel}
        className={cx(buttonBaseClassName, toneClassName, className)}
        href={href}
        onClick={onClick}
        rel={rel}
        target={target}
      >
        {children}
      </a>
    );
  }

  return (
    <button
      aria-label={ariaLabel}
      className={cx(buttonBaseClassName, toneClassName, className)}
      disabled={disabled}
      onClick={onClick}
      type={type}
    >
      {children}
    </button>
  );
};

export const PrimaryButton: React.FC<ButtonLikeProps> = (props) => <ButtonLike {...props} tone="primary" />;
export const SecondaryButton: React.FC<ButtonLikeProps> = (props) => <ButtonLike {...props} tone="secondary" />;
export const GhostButton: React.FC<ButtonLikeProps> = (props) => <ButtonLike {...props} tone="ghost" />;

import { ClientPlatformType, detectClientPlatform } from '../../utils/clientPlatform';

export const AppStoreBadges: React.FC<{
  className?: string;
  badgeHeightClass?: string;
  platform?: ClientPlatformType | 'all';
}> = memo(({ className = '', badgeHeightClass = 'h-10 sm:h-11', platform }) => {
  const [detected, setDetected] = useState<ClientPlatformType>('desktop');

  useEffect(() => {
    setDetected(detectClientPlatform());
  }, []);

  const effectivePlatform = platform || detected;
  const showIos = effectivePlatform === 'all' || effectivePlatform === 'ios' || (effectivePlatform === 'desktop' && platform !== 'android');
  const showAndroid = effectivePlatform === 'all' || effectivePlatform === 'android';

  return (
    <div className={`flex items-center justify-center gap-2.5 sm:gap-3.5 ${className}`}>
      {showIos && (
        <a
          href={IOS_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Download Clero on the Apple App Store"
          className="inline-block shrink-0 transition-transform duration-150 hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
        >
          <img
            src="/app_store_official_badge.png"
            alt="Download on the App Store"
            className={`${badgeHeightClass} w-auto object-contain block`}
            loading="lazy"
          />
        </a>
      )}
      {showAndroid && (
        <a
          href={ANDROID_INVITE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Get Clero on Google Play"
          className="inline-block shrink-0 transition-transform duration-150 hover:opacity-90 hover:scale-[1.02] active:scale-[0.98]"
        >
          <img
            src="/google_play_official_badge.png"
            alt="Get it on Google Play"
            className={`${badgeHeightClass} w-auto object-contain block`}
            loading="lazy"
          />
        </a>
      )}
    </div>
  );
});
AppStoreBadges.displayName = 'AppStoreBadges';

export const InstitutionalTrustBar: React.FC<{
  className?: string;
  maxWidthClass?: string;
}> = memo(({ className = '', maxWidthClass = 'max-w-5xl' }) => {
  return (
    <section className={`bg-slate-50/70 py-6 sm:py-8 border-y border-slate-200/60 ${className}`}>
      <div className={`mx-auto ${maxWidthClass} px-4 sm:px-6 lg:px-8`}>
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          {/* Astra Banking Partner */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-2.5 sm:gap-3.5 rounded-2xl bg-white p-3.5 sm:p-4 shadow-[0_2px_10px_rgba(15,23,42,0.03)] border border-slate-100/90 transition hover:shadow-md">
            <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-white">
              <AstraMarkOnly className="h-5 sm:h-5.5 w-auto" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400">Banking Partner</p>
              <p className="text-xs sm:text-sm font-extrabold text-slate-950 truncate">Astra Financial</p>
            </div>
          </div>

          {/* Plaid Verification */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-2.5 sm:gap-3.5 rounded-2xl bg-white p-3.5 sm:p-4 shadow-[0_2px_10px_rgba(15,23,42,0.03)] border border-slate-100/90 transition hover:shadow-md">
            <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl bg-slate-950 text-white">
              <PlaidMarkOnly className="h-4 sm:h-5 w-auto" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400">Bank Authentication</p>
              <p className="text-xs sm:text-sm font-extrabold text-slate-950 truncate">Verified by Plaid</p>
            </div>
          </div>

          {/* Bank-Grade Security */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-2.5 sm:gap-3.5 rounded-2xl bg-white p-3.5 sm:p-4 shadow-[0_2px_10px_rgba(15,23,42,0.03)] border border-slate-100/90 transition hover:shadow-md">
            <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-[var(--clero-primary)]">
              <ShieldCheck className="h-5 sm:h-5.5 w-5 sm:w-5.5" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400">Bank-Grade</p>
              <p className="text-xs sm:text-sm font-extrabold text-slate-950 truncate">256-Bit Encryption</p>
            </div>
          </div>

          {/* Direct to Bank & No Wallet Transfer Fees */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-2.5 sm:gap-3.5 rounded-2xl bg-white p-3.5 sm:p-4 shadow-[0_2px_10px_rgba(15,23,42,0.03)] border border-slate-100/90 transition hover:shadow-md">
            <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-700 font-black text-sm sm:text-base">
              $0
            </div>
            <div className="min-w-0">
              <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-slate-400">Direct to Bank</p>
              <p className="text-xs sm:text-sm font-extrabold text-slate-950 truncate">No Wallet Transfer Fees</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});
InstitutionalTrustBar.displayName = 'InstitutionalTrustBar';

export const PageShell: React.FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cx('public-page-shell min-h-screen selection:bg-teal-100 selection:text-teal-900', className)}>
    {children}
  </div>
);

export const HeroSection: React.FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <section className={cx('public-hero-mesh px-5 pb-16 pt-10 sm:px-6 md:pb-24 lg:pt-12', className)}>
    <div className="mx-auto max-w-[var(--clero-max-content)]">{children}</div>
  </section>
);

export const SurfaceCard: React.FC<{ children: ReactNode; className?: string }> = ({ children, className }) => (
  <div className={cx('public-surface-card rounded-[var(--clero-radius-2xl)]', className)}>{children}</div>
);

type ProductSurfaceTone = 'dark' | 'light';
type ProductFrameMotionState = {
  label: string;
  value: string;
  alternateValue?: string;
  detail?: string;
  alternateDetail?: string;
  align?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  semanticSource?: 'receipt_scan' | 'claim_breakdown' | 'settlement_preview' | 'groups' | 'checkout' | 'dashboard' | 'payer' | 'purchase_summary' | 'wallet' | 'bank_transfer';
};
type EditorialLayoutVariant = 'default' | 'receipt-scan' | 'hero-receipt' | 'claim-stack' | 'claim-slider' | 'payer-card' | 'hero-payer' | 'settlement-preview' | 'group-system' | 'utility-rail' | 'purchase-summary' | 'wallet-transactions' | 'bank-transfer' | 'group-settlement';
type FrameContentMode = 'mock' | 'placeholder';

const FrameStatePill: React.FC<{
  state: ProductFrameMotionState;
  isDark: boolean;
  isMotionActive?: boolean;
}> = ({ isDark, isMotionActive = false, state }) => {
  const value = isMotionActive && state.alternateValue ? state.alternateValue : state.value;
  const detail = isMotionActive && state.alternateDetail ? state.alternateDetail : state.detail;
  const alignClassName = {
    'top-left': 'left-4 top-4',
    'top-right': 'right-4 top-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-right': 'bottom-4 right-4',
  }[state.align || 'bottom-left'];

  return (
    <div
      className={cx(
        'pointer-events-none absolute z-20 min-w-[128px] rounded-[16px] border px-2.5 py-2 shadow-[0_18px_44px_rgba(2,8,23,0.18)] backdrop-blur-xl',
        alignClassName,
        isDark ? 'border-white/[0.09] bg-[#0e1720]/82 text-white' : 'border-white/85 bg-white/92 text-slate-950'
      )}
    >
      <div className="product-state-swap" key={`${state.label}-${value}-${detail || ''}`}>
        <div className="flex items-center gap-1.5">
          <span className={cx('h-1.5 w-1.5 rounded-full', isDark ? 'bg-teal-300' : 'bg-teal-500')} />
          <p className={cx('text-[7px] font-medium uppercase tracking-[0.24em]', isDark ? 'text-slate-400' : 'text-slate-500')}>
            {state.label}
          </p>
        </div>
        <p className={cx('mt-1 text-[15px] font-semibold tracking-[-0.035em] tabular-nums', isDark ? 'text-white' : 'text-slate-950')}>
          {value}
        </p>
        {detail ? <p className={cx('mt-0.5 text-[10px] leading-4', isDark ? 'text-slate-400' : 'text-slate-500')}>{detail}</p> : null}
      </div>
    </div>
  );
};

const AppAvatarStack: React.FC<{
  isDark: boolean;
  people: Array<{ label: string; color: string }>;
  size?: 'sm' | 'md';
}> = ({ isDark, people, size = 'md' }) => (
  <div className={cx('flex', size === 'sm' ? '-space-x-1.5' : '-space-x-2')}>
    {people.map((person) => (
      <div
        className={cx(
          size === 'sm'
            ? 'flex h-6 w-6 items-center justify-center rounded-full border-2 text-[8px] font-semibold tracking-[-0.02em]'
            : 'flex h-7 w-7 items-center justify-center rounded-full border-2 text-[9px] font-semibold tracking-[-0.02em]',
          isDark ? 'text-slate-950' : 'text-slate-700'
        )}
        key={person.label}
        style={{
          backgroundColor: person.color,
          borderColor: isDark ? '#111B25' : '#FFFFFF',
        }}
      >
        {person.label}
      </div>
    ))}
  </div>
);

const AppPurchaseSummaryCard: React.FC<{
  isDark: boolean;
  shareLabel: string;
  shareValue: string;
  payerMode?: boolean;
  compact?: boolean;
}> = ({ compact = false, isDark, payerMode = false, shareLabel, shareValue }) => {
  const rows = compact
    ? [
        ['Subtotal', '$196.00'],
        ['Tax', '$11.76'],
        ['Tip', '$11.24'],
        ['Total Purchase', '$219.00'],
      ]
    : [
        ['Subtotal', '$196.00'],
        ['Tax', '$11.76'],
        ['Tip', '$11.24'],
        ['Total Purchase', '$219.00'],
        ...(payerMode ? [['Payment method', 'Bank ACH']] : []),
      ];

  return (
    <div
      className={cx(
        'rounded-[18px] border px-4 py-4',
        isDark
          ? 'border-white/[0.08] bg-white/[0.05]'
          : 'border-[#E2E8F0] bg-[#F8FAFC]'
      )}
    >
      <div className="mb-4 flex items-center gap-3">
        <div
          className={cx(
            'flex h-10 w-10 items-center justify-center rounded-[12px] border',
            isDark ? 'border-white/[0.08] bg-white/[0.07] text-white' : 'border-[#E2E8F0] bg-white text-slate-900'
          )}
        >
          <Receipt className="h-4 w-4" />
        </div>
        <p className={cx('text-[15px] font-semibold tracking-[-0.02em]', isDark ? 'text-white' : 'text-slate-900')}>
          Summary
        </p>
      </div>

      <div className="space-y-3">
        {rows.map(([label, value]) => (
          <div className="flex items-center justify-between" key={label}>
            <span className={cx('text-[13px] font-medium', isDark ? 'text-slate-400' : 'text-slate-500')}>{label}</span>
            <span className={cx('text-[14px] font-semibold tabular-nums', isDark ? 'text-white' : 'text-slate-900')}>{value}</span>
          </div>
        ))}
      </div>

      <div className={cx('my-3 h-px', isDark ? 'bg-white/[0.08]' : 'bg-slate-200/80')} />

      <div className="flex items-center justify-between">
        <span className={cx('text-[13px] font-extrabold', isDark ? 'text-slate-100' : 'text-slate-900')}>{shareLabel}</span>
        <span className="text-[15px] font-black tabular-nums text-[var(--clero-primary)]">{shareValue}</span>
      </div>
    </div>
  );
};

const OrganizerHeroModule: React.FC<{
  isDark: boolean;
  isMotionActive?: boolean;
  embedded?: boolean;
  motionStage?: 'two-paid' | 'three-paid' | 'four-paid' | 'settled';
}> = ({
  embedded = false,
  isDark,
  isMotionActive = false,
  motionStage,
}) => {
  const stage = motionStage ?? (isMotionActive ? 'settled' : 'two-paid');
  const isThreePaid = stage === 'three-paid';
  const isFourPaid = stage === 'four-paid';
  const isSettled = stage === 'settled';
  const paidLabel = isSettled || isFourPaid ? '4 of 4 paid' : isThreePaid ? '3 of 4 paid' : '2 of 4 paid';
  const progressWidth = isSettled || isFourPaid ? '100%' : isThreePaid ? '76%' : '52%';
  const progressLabel = isSettled ? '100% collected' : isFourPaid ? 'All payments received' : isThreePaid ? '76% collected' : '2 payments received';
  const payoutTitle = isSettled ? 'Deposited to Chase' : isFourPaid ? 'Auto-transfer ready' : isThreePaid ? 'Bank processing' : 'Transfers begin as payments arrive';
  const payoutDetail = isSettled ? 'funds landed automatically' : 'Chase ending 1024';

  return (
  <div className={cx(embedded ? 'h-full' : 'h-full rounded-[24px] border p-4 shadow-[0_24px_60px_rgba(2,8,23,0.08)]', embedded ? '' : isDark ? 'border-white/[0.08] bg-[#0f1822]' : 'border-[#E4E6E4] bg-[#F3F4F2]')}>
    <div className={cx(embedded ? 'space-y-3 px-0 py-0' : 'space-y-3')}>
      <div>
        <p className={cx('text-[9px] font-semibold uppercase tracking-[0.24em]', isDark ? 'text-slate-400' : 'text-slate-500')}>
          ORGANIZER DASHBOARD
        </p>
        <p className={cx('mt-2 text-[17px] font-semibold tracking-[-0.035em]', isDark ? 'text-white' : 'text-[#0F172A]')}>
          Test Split
        </p>
      </div>

      <div className="w-full rounded-[22px] border border-white/10 bg-[linear-gradient(145deg,#1E293B_0%,#334155_100%)] px-4 py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[13px] font-semibold tracking-[-0.02em] text-slate-200">To receive</p>
          <div className={cx('rounded-full px-3 py-1 text-[10px] font-semibold tracking-[-0.01em]', isSettled ? 'bg-emerald-400/14 text-emerald-200' : 'bg-white/[0.08] text-slate-300')}>
            {isSettled ? 'Collected' : 'Active'}
          </div>
        </div>

        <p className="mt-3 text-[28px] font-semibold leading-none tracking-[-0.05em] text-white tabular-nums">$184.00</p>

        <div className="mt-4 h-2.5 rounded-full bg-white/14">
          <div
            className="h-full rounded-full bg-[#0D9488] transition-all duration-500"
            style={{ width: progressWidth }}
          />
        </div>

        <p className="mt-2.5 text-[10px] font-medium text-slate-300">{progressLabel}</p>

        <div className="mt-4 grid grid-cols-2 gap-5">
          <div>
            <p className="text-[10px] font-semibold text-slate-300">Paid</p>
            <p className="mt-1 text-[12px] font-semibold tracking-[-0.03em] text-white tabular-nums whitespace-nowrap">
              {paidLabel}
            </p>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-slate-300">Your portion</p>
            <p className="mt-1 text-[12px] font-semibold tracking-[-0.03em] text-white tabular-nums whitespace-nowrap">$35.00</p>
          </div>
        </div>
      </div>

      <div className="w-full rounded-[18px] border border-[#D6DBE4] bg-[#F7F8FA] px-3 py-3 shadow-[0_2px_0_rgba(255,255,255,0.7)]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <AppAvatarStack
              isDark={false}
              people={[
                { label: 'AS', color: '#D9DDE3' },
                { label: 'JD', color: '#6EE7A2' },
                { label: 'MA', color: '#7DD3FC' },
                { label: 'NI', color: '#CBD5E1' },
              ]}
              size="sm"
            />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold tracking-[-0.03em] text-slate-900">Participants</p>
              <p className="mt-0.5 text-[10px] text-slate-500">4 people in this split</p>
            </div>
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" />
        </div>
      </div>

      <div className="rounded-[16px] border border-[#D6DBE4] bg-[#F7F8FA] px-3 py-2.5 shadow-[0_2px_0_rgba(255,255,255,0.7)]">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-500">Payout</p>
            <p className="mt-1 text-[11px] font-semibold tracking-[-0.02em] text-slate-900">{payoutTitle}</p>
          </div>
          <p className="text-[10px] text-slate-500">{payoutDetail}</p>
        </div>
      </div>

      <AppPurchaseSummaryCard compact isDark={false} shareLabel="Total Expected Back (Net)" shareValue="$184.00" />
    </div>
  </div>
);
};

const ClaimItemCard: React.FC<{
  isDark: boolean;
  title: string;
  amount: string;
  icon: string;
  summary: string;
  subsummary?: string;
  state: 'claimed' | 'unclaimed' | 'pending' | 'locked';
  expanded?: boolean;
  compact?: boolean;
}> = ({ amount, compact = false, expanded = false, icon, isDark, state, subsummary, summary, title }) => {
  const barClassName =
    state === 'claimed'
      ? 'bg-teal-400'
      : state === 'pending'
        ? 'bg-amber-300'
        : state === 'locked'
          ? (isDark ? 'bg-slate-400/60' : 'bg-slate-300')
          : (isDark ? 'bg-white/[0.12]' : 'bg-slate-200');

  return (
    <div
      className={cx(
        compact ? 'relative overflow-hidden rounded-[18px] border px-3 py-3' : 'relative overflow-hidden rounded-[20px] border px-4 py-4',
        isDark ? 'border-white/[0.08] bg-white/[0.04]' : 'border-[#E2E8F0] bg-white'
      )}
    >
      <div className={cx('absolute inset-y-0 left-0 w-1.5', barClassName)} />
      <div className={cx(compact ? 'space-y-2.5 pl-1.5' : 'space-y-3 pl-2')}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-3">
            <div className={cx(compact ? 'flex h-9 w-9 items-center justify-center rounded-[12px] text-[15px]' : 'flex h-10 w-10 items-center justify-center rounded-[14px] text-[16px]', isDark ? 'bg-white/[0.07]' : 'bg-slate-100')}>
              {icon}
            </div>
            <div className="min-w-0">
              <div className={cx('flex items-center gap-2', compact ? 'max-w-[138px]' : '')}>
                <p className={cx(compact ? 'truncate text-[12px] font-semibold tracking-[-0.02em]' : 'truncate text-[14px] font-semibold tracking-[-0.02em]', isDark ? 'text-white' : 'text-slate-900')}>{title}</p>
                {state === 'locked' ? (
                  <span className={cx('rounded-full px-2 py-0.5 text-[8px] font-semibold uppercase tracking-[0.18em]', isDark ? 'bg-white/[0.08] text-slate-300' : 'bg-slate-100 text-slate-600')}>
                    Locked
                  </span>
                ) : null}
              </div>
              <p className={cx(compact ? 'mt-1 text-[11px] font-medium tabular-nums' : 'mt-1 text-[13px] font-medium tabular-nums', isDark ? 'text-slate-300' : 'text-slate-500')}>{amount}</p>
            </div>
          </div>
          <div className="min-w-[72px] text-right">
            <p className={cx(compact ? 'text-[11px] font-semibold leading-4' : 'text-[12px] font-semibold', isDark ? 'text-white' : 'text-slate-900')}>{summary}</p>
            {subsummary ? <p className={cx(compact ? 'mt-1 text-[10px] leading-4' : 'mt-1 text-[11px]', isDark ? 'text-slate-400' : 'text-slate-500')}>{subsummary}</p> : null}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3">
          <AppAvatarStack
            isDark={isDark}
            size={compact ? 'sm' : 'md'}
            people={[
              { label: 'MA', color: '#5EEAD4' },
              { label: 'AS', color: '#7DD3FC' },
              { label: 'NI', color: '#CBD5E1' },
            ]}
          />
          <p className={cx(compact ? 'text-[10px] leading-4' : 'text-[11px]', isDark ? 'text-slate-400' : 'text-slate-500')}>
            {state === 'claimed' ? 'Fully claimed' : state === 'pending' ? 'waiting for claim' : state === 'locked' ? 'claim locked' : 'Unclaimed: $9.30'}
          </p>
        </div>

        {expanded ? (
          <div className={cx(compact ? 'space-y-1.5 border-t pt-2.5' : 'space-y-2 border-t pt-3', isDark ? 'border-white/[0.08]' : 'border-slate-200/80')}>
            {[
              ['Maya', '$9.25', '50%'],
              ['Ash', '$9.25', '50%'],
            ].map(([name, value, percent]) => (
              <div className="flex items-center justify-between" key={name}>
                <span className={cx(compact ? 'text-[10px]' : 'text-[12px]', isDark ? 'text-slate-300' : 'text-slate-600')}>{name}</span>
                <span className={cx(compact ? 'text-[10px] font-medium' : 'text-[12px] font-medium', isDark ? 'text-white' : 'text-slate-900')}>{value} • {percent}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

const PayerHeroModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div
    className={cx(
      'h-full rounded-[24px] border p-5 shadow-[0_24px_60px_rgba(2,8,23,0.08)]',
      isDark
        ? 'border-white/[0.08] bg-[linear-gradient(160deg,#203142_0%,#32475b_100%)]'
        : 'border-[#E2E8F0] bg-[linear-gradient(160deg,#FFFFFF_0%,#F4F7FA_100%)]'
    )}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <p className={cx('text-[10px] font-semibold uppercase tracking-[0.22em]', isDark ? 'text-slate-300' : 'text-slate-500')}>PAYMENT DETAILS</p>
        <p className={cx('mt-3 text-[11px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>You owe</p>
        <p className={cx('mt-2 text-[34px] font-semibold tracking-[-0.05em] tabular-nums', isDark ? 'text-white' : 'text-slate-950')}>$41.29</p>
        <p className={cx('mt-2 text-[13px]', isDark ? 'text-slate-300' : 'text-slate-600')}>To Maya</p>
      </div>
      <div className={cx('rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em]', isMotionActive ? (isDark ? 'bg-emerald-400/14 text-emerald-200' : 'bg-emerald-50 text-emerald-700') : (isDark ? 'bg-amber-300/14 text-amber-200' : 'bg-amber-50 text-amber-700'))}>
        {isMotionActive ? (
          <span className="inline-flex items-center gap-1"><Check className="h-3.5 w-3.5" />Paid</span>
        ) : 'Open'}
      </div>
    </div>

    <div className="mt-5 grid grid-cols-2 gap-3">
      <div className={cx('rounded-[16px] p-3', isDark ? 'bg-white/[0.08]' : 'bg-white')}>
        <p className={cx('text-[9px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Claimed</p>
        <p className={cx('mt-2 text-sm font-semibold', isDark ? 'text-white' : 'text-slate-950')}>{isMotionActive ? '4 items' : '3 items'}</p>
      </div>
      <div className={cx('rounded-[16px] p-3', isDark ? 'bg-white/[0.08]' : 'bg-white')}>
        <p className={cx('text-[9px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Action</p>
        <p className={cx('mt-2 text-sm font-semibold', isDark ? 'text-white' : 'text-slate-950')}>{isMotionActive ? 'Completed' : 'Claim Items'}</p>
      </div>
    </div>

    <div className="mt-4">
      <AppPurchaseSummaryCard compact isDark={isDark} payerMode shareLabel="Your Share" shareValue="$41.29" />
    </div>
  </div>
);

const HeroPayerModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div
    className={cx(
      'h-full rounded-[24px] border p-4 shadow-[0_24px_60px_rgba(2,8,23,0.08)]',
      isDark
        ? 'border-white/[0.08] bg-[linear-gradient(160deg,#203142_0%,#32475b_100%)]'
        : 'border-[#E2E8F0] bg-[linear-gradient(160deg,#FFFFFF_0%,#F4F7FA_100%)]'
    )}
  >
    <div className="grid h-full grid-cols-[0.88fr_1.12fr] gap-3">
      <div className={cx('rounded-[18px] p-3.5', isDark ? 'bg-[#0d1722]/90' : 'bg-white')}>
        <p className={cx('text-[9px] font-medium uppercase tracking-[0.2em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Payer view</p>
        <p className={cx('mt-2 text-[15px] font-semibold tracking-[-0.03em]', isDark ? 'text-white' : 'text-slate-950')}>You owe Maya</p>
        <p className={cx('mt-2.5 text-[11px] leading-4', isDark ? 'text-slate-300' : 'text-slate-600')}>
          {isMotionActive ? 'payment completed by link' : 'paying directly by link'}
        </p>
      </div>

      <div className="flex min-w-0 flex-col justify-between">
        <div className="flex justify-end">
          <div className={cx('rounded-full px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.18em]', isMotionActive ? (isDark ? 'bg-emerald-400/14 text-emerald-200' : 'bg-emerald-50 text-emerald-700') : (isDark ? 'bg-amber-300/14 text-amber-200' : 'bg-amber-50 text-amber-700'))}>
            {isMotionActive ? (
              <span className="inline-flex items-center gap-1"><Check className="h-3 w-3" />Paid</span>
            ) : 'Open'}
          </div>
        </div>

        <div className="min-w-0 pt-1">
          <p className={cx('text-[28px] font-semibold leading-none tracking-[-0.05em] tabular-nums', isDark ? 'text-white' : 'text-slate-950')}>$41.29</p>
          <p className={cx('mt-2 text-[11px] leading-4 font-medium', isDark ? 'text-slate-300' : 'text-slate-600')}>
            {isMotionActive ? 'paid to Maya' : 'pay by link to Maya'}
          </p>
        </div>
      </div>
    </div>
  </div>
);

const ClaimDeviceModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div className="flex h-full flex-col gap-2.5">
    <div className="flex items-center gap-2.5">
      <div className={cx('flex h-9 w-9 items-center justify-center rounded-full border', isDark ? 'border-white/[0.08] bg-white/[0.04] text-white' : 'border-[#E2E8F0] bg-white text-slate-900')}>
        <ChevronRight className="h-4 w-4 rotate-180" />
      </div>
      <p className={cx('text-[10px] font-semibold uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>PAYMENT DETAILS</p>
    </div>

    <p className={cx('text-[16px] font-semibold leading-[1.05] tracking-[-0.04em]', isDark ? 'text-white' : 'text-slate-950')}>COBA Viet Kitchen & Ales</p>

    <div className={cx('rounded-[20px] border px-4 py-3.5 shadow-[0_20px_48px_rgba(2,8,23,0.1)]', isDark ? 'border-white/[0.08] bg-[linear-gradient(160deg,#223247_0%,#3D4F66_100%)]' : 'border-[#E2E8F0] bg-[linear-gradient(160deg,#223247_0%,#3D4F66_100%)]')}>
      <div className="flex items-center gap-2">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#A3B2C6]">You owe</p>
        <div className="rounded-full bg-amber-300/14 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.2em] text-amber-200">Open</div>
      </div>
      <p className="mt-2.5 text-[28px] font-semibold tracking-[-0.06em] text-white">$41.29</p>
      <p className="mt-1.5 text-[11px] font-medium text-[#8FA0B5]">To Dasdasd</p>
    </div>

    <div className={cx('flex items-center justify-between rounded-[18px] border px-3.5 py-3', isDark ? 'border-white/[0.08] bg-white/[0.04]' : 'border-[#E2E8F0] bg-white')}>
      <div className="flex items-center gap-3">
        <AppAvatarStack
          isDark={isDark}
          people={[
            { label: 'DA', color: '#CBD5E1' },
            { label: 'AS', color: '#E9A8F5' },
            { label: 'JD', color: '#86EFAC' },
          ]}
        />
        <div>
          <p className={cx('text-[13px] font-semibold tracking-[-0.02em]', isDark ? 'text-white' : 'text-slate-900')}>Participants</p>
          <p className={cx('mt-1 text-[10px] leading-4', isDark ? 'text-slate-400' : 'text-slate-500')}>3 people in this split</p>
        </div>
      </div>
      <ChevronRight className={cx('h-4 w-4', isDark ? 'text-slate-400' : 'text-slate-400')} />
    </div>

    <div className="flex items-center justify-between gap-3">
      <p className={cx('text-[10px] font-semibold uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Receipt breakdown</p>
      <div className={cx('rounded-full px-3 py-1.5 text-[10px] font-semibold', isDark ? 'bg-white/[0.06] text-slate-200' : 'bg-slate-100 text-slate-700')}>Edit Claims</div>
    </div>

    <div className={cx('rounded-[18px] px-3 py-2', isDark ? 'bg-white/[0.04]' : 'bg-[#F8FAFC]')}>
      <p className={cx('text-[10px] font-semibold uppercase tracking-[0.2em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Your items</p>
      <div className="mt-2 space-y-2">
        <div className={cx('rounded-[18px] border px-3 py-2.5', isDark ? 'border-white/[0.08] bg-white/[0.06]' : 'border-[#E2E8F0] bg-white')}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <div className="mt-0.5 h-8 w-1 rounded-full bg-teal-400" />
              <div className={cx('flex h-8 w-8 items-center justify-center rounded-[10px] text-[15px]', isDark ? 'bg-white/[0.07]' : 'bg-slate-100')}>🍽️</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className={cx('truncate text-[11px] font-semibold tracking-[-0.02em]', isDark ? 'text-teal-200' : 'text-teal-700')}>Rice Tofu CaRi</p>
                  <p className={cx('text-[11px] font-semibold tabular-nums', isDark ? 'text-slate-300' : 'text-slate-400')}>$18.00</p>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <AppAvatarStack
                    isDark={isDark}
                    size="sm"
                    people={[
                      { label: 'YOU', color: '#14B8A6' },
                      { label: 'JD', color: '#86EFAC' },
                      { label: 'DA', color: '#CBD5E1' },
                    ]}
                  />
                  <p className={cx('text-[11px] font-semibold text-right', isDark ? 'text-white' : 'text-slate-700')}>You owe $6.00</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={cx('rounded-[18px] border px-3 py-2.5', isDark ? 'border-white/[0.08] bg-white/[0.06]' : 'border-[#E2E8F0] bg-white')}>
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 items-start gap-3">
              <div className="mt-0.5 h-8 w-1 rounded-full bg-teal-400" />
              <div className={cx('flex h-8 w-8 items-center justify-center rounded-[10px] text-[15px]', isDark ? 'bg-white/[0.07]' : 'bg-slate-100')}>🍜</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <p className={cx('truncate text-[11px] font-semibold tracking-[-0.02em]', isDark ? 'text-teal-200' : 'text-teal-700')}>Chicken Vermicelli</p>
                  <p className={cx('text-[11px] font-semibold tabular-nums', isDark ? 'text-slate-300' : 'text-slate-400')}>$23.00</p>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <AppAvatarStack
                    isDark={isDark}
                    size="sm"
                    people={[
                      { label: 'YOU', color: '#14B8A6' },
                    ]}
                  />
                  <p className={cx('text-[11px] font-semibold text-right', isDark ? 'text-white' : 'text-slate-700')}>You owe $23.00</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="mt-auto grid grid-cols-[0.9fr_1.6fr] gap-3">
      <button
        className={cx(
          'inline-flex items-center justify-center gap-2 rounded-[18px] border px-3 py-2.5 text-[11px] font-semibold transition',
          isDark ? 'border-white/[0.08] bg-white/[0.05] text-slate-200 hover:bg-white/[0.08]' : 'border-[#D1D5DB] bg-white text-slate-700 hover:bg-slate-50'
        )}
        type="button"
      >
        <X className="h-4 w-4" />
        Decline
      </button>
      <button
        className="inline-flex items-center justify-center gap-2 rounded-[18px] bg-[var(--clero-primary)] px-3 py-2.5 text-[11px] font-semibold text-white shadow-[0_16px_36px_rgba(13,148,136,0.2)] transition hover:bg-[var(--clero-primary-dark)]"
        type="button"
      >
        <span>Settle ($41.29)</span>
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  </div>
);

const ClaimStackModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div className="absolute inset-0 p-4">
    <div className="grid h-full grid-cols-[1.08fr_0.92fr] gap-4">
      <div className={cx('rounded-[24px] border p-4 shadow-[0_24px_60px_rgba(2,8,23,0.08)]', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-white')}>
        <p className={cx('mb-3 text-[10px] font-semibold uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Your Items</p>
        <div className="space-y-3">
          <ClaimItemCard
            amount="$18.50"
            compact
            expanded
            icon="🍣"
            isDark={isDark}
            state={isMotionActive ? 'claimed' : 'pending'}
            subsummary={isMotionActive ? 'You owe $9.25' : 'waiting'}
            summary={isMotionActive ? 'Claimed' : 'Pending'}
            title="Spicy tuna roll"
          />
          <ClaimItemCard
            amount="$9.30"
            compact
            icon="🍟"
            isDark={isDark}
            state={isMotionActive ? 'claimed' : 'unclaimed'}
            subsummary={isMotionActive ? 'You owe $4.65' : 'Still open'}
            summary={isMotionActive ? 'Claimed' : 'Unclaimed'}
            title="Shared fries"
          />
        </div>
      </div>

      <div className="flex h-full flex-col gap-4">
        <div className={cx('rounded-[22px] border p-4 shadow-[0_24px_60px_rgba(2,8,23,0.08)]', isDark ? 'border-white/[0.08] bg-white/[0.06]' : 'border-[#E2E8F0] bg-white')}>
          <p className={cx('text-[10px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Totals</p>
          <p className={cx('mt-3 text-[24px] font-semibold tracking-[-0.04em] tabular-nums', isDark ? 'text-white' : 'text-slate-950')}>
            {isMotionActive ? '$118.52' : '$99.72'}
          </p>
          <p className={cx('mt-2 text-[12px] leading-5', isDark ? 'text-slate-300' : 'text-slate-600')}>
            {isMotionActive ? 'locked after final claim' : 'updating as shares change'}
          </p>
        </div>

        <div className={cx('mt-auto rounded-[18px] px-4 py-3', isDark ? 'bg-white/[0.06]' : 'bg-slate-100')}>
          <div className="flex items-center justify-between gap-3">
            <span className={cx('text-[12px]', isDark ? 'text-slate-300' : 'text-slate-600')}>Unclaimed</span>
            <span className={cx('text-[13px] font-semibold tabular-nums', isDark ? 'text-white' : 'text-slate-950')}>
              {isMotionActive ? '$0.00' : '$18.30'}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const ClaimSliderModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => {
  const livePercent = isMotionActive ? 33 : 0;
  const youAmount = isMotionActive ? '$6.00' : '$0.00';

  return (
    <div className="absolute inset-0 p-4">
      <div className={cx('flex h-full flex-col rounded-[28px] border px-5 py-4 shadow-[0_30px_80px_rgba(2,8,23,0.12)]', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-white')}>
        <div className="flex items-center justify-between gap-3">
          <p className={cx('text-[22px] font-semibold tracking-[-0.04em]', isDark ? 'text-white' : 'text-slate-950')}>Select Your Items</p>
          <div className={cx('flex h-9 w-9 items-center justify-center rounded-full border', isDark ? 'border-white/[0.08] bg-white/[0.04] text-white' : 'border-[#E2E8F0] bg-white text-slate-900')}>
            <X className="h-4 w-4" />
          </div>
        </div>

        <p className={cx('mt-3 text-[14px] leading-6', isDark ? 'text-slate-300' : 'text-slate-500')}>Tap to claim items or use the slider to set your share.</p>

        <div className={cx('mt-3 rounded-[18px] px-4 py-3', isDark ? 'bg-white/[0.06]' : 'bg-slate-100')}>
          <p className={cx('text-[13px] font-semibold', isDark ? 'text-white' : 'text-slate-700')}>Organizer pre-selected items for you</p>
          <p className={cx('mt-1 text-[12px]', isDark ? 'text-slate-300' : 'text-slate-500')}>You can edit before sending.</p>
        </div>

        <div className={cx('mt-3 rounded-[18px] border px-4 py-3', isDark ? 'border-white/[0.08] bg-white/[0.04]' : 'border-[#D7DFEA] bg-[#FAFCFF]')}>
          <p className={cx('text-[13px] font-semibold', isDark ? 'text-white' : 'text-slate-500')}>You&apos;ve selected {isMotionActive ? 4 : 1} items — {isMotionActive ? '$37.00' : '$0.00'}</p>
        </div>

        <div className={cx('mt-3 rounded-[24px] border px-4 py-4', isDark ? 'border-white/[0.08] bg-white/[0.04]' : 'border-[#D7DFEA] bg-white')}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 flex-1 items-start gap-4">
              <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-full bg-[var(--clero-primary)] text-white">
                <Check className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className={cx('text-[16px] font-semibold tracking-[-0.03em]', isDark ? 'text-white' : 'text-slate-900')}>Rice Tofu CaRi</p>
                <p className={cx('mt-0.5 text-[14px] font-medium', isDark ? 'text-slate-300' : 'text-slate-400')}>$18.00</p>
              </div>
            </div>
            <div className={cx('rounded-full px-3 py-1.5 text-[14px] font-semibold tabular-nums', isDark ? 'bg-teal-400/14 text-teal-200' : 'bg-[var(--clero-primary)] text-white')}>
              {youAmount}
            </div>
          </div>

          <p className={cx('mt-4 text-[15px] font-semibold', isDark ? 'text-white' : 'text-slate-900')}>
            Your share: {youAmount} <span className={cx('font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>({livePercent}%)</span>
          </p>

          <div className="mt-3 flex items-center justify-between gap-4">
            <p className={cx('text-[13px] font-semibold', isDark ? 'text-slate-400' : 'text-slate-500')}>Split with</p>
            <div className="flex items-center gap-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[var(--clero-primary)] text-[12px] font-bold text-white">You</div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#94A3B8] text-[12px] font-bold text-white">DA</div>
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#CBD5E1] text-[12px] font-bold text-slate-700">JD</div>
            </div>
          </div>

          <div className="relative mt-4 h-8">
            <div className={cx('absolute inset-x-0 top-1/2 h-2.5 -translate-y-1/2 overflow-hidden rounded-full', isDark ? 'bg-white/[0.08]' : 'bg-[#CBD5E1]')}>
              <div className="absolute inset-y-0 left-0 rounded-full bg-[var(--clero-primary)]" style={{ width: `${livePercent}%` }} />
              <div className="absolute inset-y-0 bg-[#94A3B8]" style={{ left: `${livePercent}%`, width: '18%' }} />
            </div>
            <div
              className="absolute top-1/2 h-7 w-7 -translate-y-1/2 rounded-full bg-[var(--clero-primary)] shadow-[0_10px_24px_rgba(13,148,136,0.28)]"
              style={{ left: `calc(${livePercent}% - 14px)` }}
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2.5">
            {[25, 50, 75, 100].map((target) => (
              <div
                className={cx(
                  'rounded-full border px-3 py-1.5 text-[12px] font-semibold',
                  target === 100 && isMotionActive
                    ? (isDark ? 'border-teal-300/30 bg-teal-400/10 text-teal-200' : 'border-teal-200 bg-teal-50 text-teal-700')
                    : (isDark ? 'border-white/[0.08] bg-white/[0.04] text-slate-300' : 'border-[#D1D5DB] bg-white text-slate-600')
                )}
                key={target}
              >
                {target}%
              </div>
            ))}
            <div className={cx('rounded-full border px-4 py-1.5 text-[12px] font-semibold', isDark ? 'border-teal-300/30 bg-teal-400/10 text-teal-200' : 'border-teal-200 bg-teal-50 text-teal-700')}>
              Claim remaining
            </div>
          </div>
        </div>

        <div className={cx('mt-3 rounded-[22px] border px-4 py-4', isDark ? 'border-white/[0.08] bg-white/[0.04]' : 'border-[#D7DFEA] bg-white')}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className={cx('h-9 w-9 rounded-full border-2', isDark ? 'border-white/[0.16]' : 'border-[#D1D5DB]')} />
              <div>
                <p className={cx('text-[16px] font-semibold tracking-[-0.03em]', isDark ? 'text-white' : 'text-slate-900')}>Chicken Vermicelli</p>
                <p className={cx('mt-0.5 text-[14px] font-medium', isDark ? 'text-slate-300' : 'text-slate-400')}>$23.00</p>
              </div>
            </div>
            <div className={cx('rounded-full px-3 py-1.5 text-[13px] font-semibold', isDark ? 'bg-white/[0.08] text-slate-300' : 'bg-slate-100 text-slate-500')}>Dasdasd</div>
          </div>
        </div>

        <div className="mt-auto rounded-[22px] bg-black px-6 py-4 text-center text-[18px] font-semibold text-white shadow-[0_24px_60px_rgba(2,8,23,0.18)]">
          Done
        </div>
      </div>
    </div>
  );
};

const ReceiptAnalysisModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div className="absolute inset-0 grid grid-cols-[1.28fr_0.92fr] gap-4 p-4">
    <div className="-translate-x-6 translate-y-2">
      <div className={cx('relative h-full overflow-hidden rounded-[28px] border shadow-[0_30px_80px_rgba(2,8,23,0.1)]', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-white')}>
        <div className={cx('absolute inset-0', isDark ? 'bg-[linear-gradient(180deg,rgba(255,255,255,0.06)_0%,rgba(255,255,255,0)_100%)]' : 'bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(248,250,252,0.96)_100%)]')} />
        <div className="absolute inset-x-5 top-5">
          <div className="flex items-center justify-between">
            <div>
              <p className={cx('text-[10px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Analyzing Receipt</p>
              <p className={cx('mt-2 text-[20px] font-semibold tracking-[-0.04em]', isDark ? 'text-white' : 'text-slate-950')}>Dinner on Main</p>
            </div>
            <div className={cx('rounded-full px-3 py-1 text-[9px] font-semibold uppercase tracking-[0.22em]', isDark ? 'bg-teal-400/12 text-teal-200' : 'bg-teal-50 text-teal-700')}>
              {isMotionActive ? 'Itemized' : 'Scanning'}
            </div>
          </div>
        </div>

        <div className="absolute inset-x-5 top-24 space-y-2.5">
          {[
            ['Spicy tuna roll', '$18.50'],
            ['House margarita', '$14.00'],
            ['Shared fries', '$9.30'],
            ['Subtotal', '$68.20'],
            ['Tax', '$4.88'],
            ['Tip', '$6.60'],
            ['Total', '$79.68'],
          ].map(([label, value]) => (
            <div className={cx('flex items-center justify-between rounded-[14px] px-3 py-2.5', isDark ? 'bg-white/[0.05]' : 'bg-slate-50')} key={label}>
              <span className={cx('text-[13px]', isDark ? 'text-slate-300' : 'text-slate-600')}>{label}</span>
              <span className={cx('text-[13px] font-semibold tabular-nums', isDark ? 'text-white' : 'text-slate-950')}>{value}</span>
            </div>
          ))}
        </div>

        <div className="receipt-scan-line absolute inset-x-4 top-16 h-[2px] rounded-full bg-teal-400/80 shadow-[0_0_14px_rgba(45,212,191,0.8)]" />
        <div className="receipt-scan-glow absolute inset-x-0 top-0 h-32 bg-teal-300/10 blur-2xl" />
      </div>
    </div>

    <div className="space-y-3 pt-4">
      <div className={cx('rounded-[18px] border px-4 py-4', isDark ? 'border-white/[0.08] bg-white/[0.06]' : 'border-[#E2E8F0] bg-white')}>
        <p className={cx('text-[10px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>AI result</p>
        <p className={cx('mt-3 text-base font-semibold tracking-[-0.02em]', isDark ? 'text-white' : 'text-slate-950')}>
          {isMotionActive ? 'Totals matching' : 'Reading items'}
        </p>
        <p className={cx('mt-2 text-sm leading-6', isDark ? 'text-slate-300' : 'text-slate-600')}>
          Subtotal, tax, tip, and receipt lines are being structured for the split.
        </p>
      </div>

      <AppPurchaseSummaryCard
        compact
        isDark={isDark}
        shareLabel="Your Share"
        shareValue={isMotionActive ? '$41.29' : 'Claim items'}
      />

      <div className={cx('rounded-[18px] px-4 py-3', isDark ? 'bg-white/[0.05]' : 'bg-slate-100')}>
        <p className={cx('text-[11px]', isDark ? 'text-slate-400' : 'text-slate-500')}>
          {isMotionActive ? '11 lines detected. Totals matching.' : 'OCR reading items, tax, and tip.'}
        </p>
      </div>
    </div>
  </div>
);

const HeroReceiptModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div className="absolute inset-0 grid grid-cols-[0.96fr_1.04fr] gap-3 p-3.5">
    <div className={cx('relative overflow-hidden rounded-[22px] border px-3.5 py-3.5', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-white')}>
      <div className={cx('absolute inset-0', isDark ? 'bg-[linear-gradient(180deg,rgba(255,255,255,0.06)_0%,rgba(255,255,255,0)_100%)]' : 'bg-[linear-gradient(180deg,rgba(255,255,255,0.94)_0%,rgba(248,250,252,0.94)_100%)]')} />
      <div className="relative z-10">
        <p className={cx('text-[9px] font-medium uppercase tracking-[0.2em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Analyzing receipt</p>
        <p className={cx('mt-3 max-w-[84px] text-[15px] font-semibold leading-[1.1] tracking-[-0.035em]', isDark ? 'text-white' : 'text-slate-950')}>Dinner selected</p>
      </div>
      <div className="receipt-scan-line absolute inset-x-4 top-[52%] h-[2px] rounded-full bg-teal-400/80 shadow-[0_0_14px_rgba(45,212,191,0.8)]" />
      <div className="receipt-scan-glow absolute inset-x-0 top-6 h-20 bg-teal-300/10 blur-2xl" />
      <div className="absolute inset-x-3.5 bottom-3.5 space-y-1.5">
        {[
          ['Total', '$79.68'],
        ].map(([label, value]) => (
          <div className={cx('flex items-center justify-between rounded-[12px] px-2.5 py-1.5', isDark ? 'bg-white/[0.05]' : 'bg-slate-50')} key={label}>
            <span className={cx('text-[10px]', isDark ? 'text-slate-300' : 'text-slate-600')}>{label}</span>
            <span className={cx('text-[10px] font-semibold tabular-nums', isDark ? 'text-white' : 'text-slate-950')}>{value}</span>
          </div>
        ))}
      </div>
    </div>

    <div className={cx('rounded-[22px] border px-3.5 py-3.5', isDark ? 'border-white/[0.08] bg-white/[0.06]' : 'border-[#E2E8F0] bg-white')}>
      <p className={cx('text-[9px] font-medium uppercase tracking-[0.2em]', isDark ? 'text-slate-400' : 'text-slate-500')}>AI result</p>
      <p className={cx('mt-3 text-[15px] font-semibold tracking-[-0.03em]', isDark ? 'text-white' : 'text-slate-950')}>
        {isMotionActive ? 'Totals matching' : 'AI itemized'}
      </p>
      <div className="mt-3 space-y-1.5">
        {[
          'Spicy tuna roll',
          'Tax + tip',
        ].map((item) => (
          <div className={cx('rounded-[12px] px-3 py-2', isDark ? 'bg-white/[0.05]' : 'bg-slate-50')} key={item}>
            <p className={cx('text-[10px] font-medium', isDark ? 'text-slate-200' : 'text-slate-700')}>{item}</p>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const PurchaseSummaryModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div className="absolute inset-0 p-4">
    <div className={cx('absolute inset-4 rounded-[24px] border p-5 shadow-[0_24px_60px_rgba(2,8,23,0.08)]', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-white')}>
      <AppPurchaseSummaryCard
        isDark={isDark}
        shareLabel="Total Expected Back (Net)"
        shareValue={isMotionActive ? '$184.00' : '$96.40'}
      />
      <div className={cx('mt-4 rounded-[18px] border px-4 py-4', isDark ? 'border-white/[0.08] bg-white/[0.04]' : 'border-[#E2E8F0] bg-[#F8FAFC]')}>
        <p className={cx('text-[10px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Purchase details</p>
        <div className="mt-3 space-y-3">
          {[
            ['Merchant', 'Dinner on Main'],
            ['Created on', 'Mar 7, 2026'],
            ['Payment method', 'Bank ACH'],
          ].map(([label, value]) => (
            <div className="flex items-center justify-between" key={label}>
              <span className={cx('text-[13px]', isDark ? 'text-slate-300' : 'text-slate-600')}>{label}</span>
              <span className={cx('text-[13px] font-semibold', isDark ? 'text-white' : 'text-slate-900')}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const WalletTransactionsModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div className="absolute inset-0 p-4">
    <div className={cx('absolute inset-x-4 top-4 rounded-[24px] border p-4 shadow-[0_24px_60px_rgba(2,8,23,0.08)]', isDark ? 'border-white/[0.08] bg-[linear-gradient(160deg,#1F2937_0%,#334155_100%)]' : 'border-[#E2E8F0] bg-[linear-gradient(160deg,#1F2937_0%,#334155_100%)]')}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[14px] font-semibold tracking-[-0.02em] text-white">Chase</p>
          <p className="mt-1 text-[12px] text-slate-300">Sapphire Reserve</p>
        </div>
        <p className="text-[12px] font-semibold tracking-[0.08em] text-slate-200">•••• 1024</p>
      </div>
      <div className="mt-8 flex items-center justify-between">
        <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-slate-400">Connected</p>
        <p className="text-[11px] text-slate-300">Wallet</p>
      </div>
    </div>

    <div className={cx('absolute inset-x-8 bottom-4 top-[34%] rounded-[24px] border p-4 shadow-[0_24px_60px_rgba(2,8,23,0.08)]', isDark ? 'border-white/[0.08] bg-white/[0.06]' : 'border-[#E2E8F0] bg-white')}>
      <div className="flex items-center justify-between">
        <div>
          <p className={cx('text-[16px] font-semibold tracking-[-0.03em]', isDark ? 'text-white' : 'text-slate-900')}>Transactions</p>
          <p className={cx('mt-1 text-[12px]', isDark ? 'text-slate-400' : 'text-slate-500')}>$842.16 spent this month</p>
        </div>
        <div className={cx('rounded-full border px-3 py-1 text-[10px] font-medium', isDark ? 'border-white/[0.08] bg-white/[0.04] text-slate-300' : 'border-[#E2E8F0] bg-[#F8FAFC] text-slate-500')}>
          This month
        </div>
      </div>

      <div className="mt-4 space-y-3">
        {[
          ['Dinner on Main', 'Mar 7', '$79.68', true],
          ['Whole Foods', 'Mar 6', '$126.54', false],
          ['Airport Cab', 'Mar 5', '$42.00', false],
        ].map(([name, date, value, selected]) => (
          <div className={cx('flex items-center justify-between rounded-[18px] border px-4 py-3', selected ? (isDark ? 'border-teal-300/20 bg-teal-400/8' : 'border-teal-200 bg-teal-50/70') : (isDark ? 'border-white/[0.08] bg-white/[0.03]' : 'border-[#E2E8F0] bg-[#F8FAFC]'))} key={name}>
            <div>
              <p className={cx('text-[13px] font-semibold', isDark ? 'text-white' : 'text-slate-900')}>{name}</p>
              <p className={cx('mt-1 text-[11px]', isDark ? 'text-slate-400' : 'text-slate-500')}>{date}</p>
            </div>
            <div className="flex items-center gap-3">
              <p className={cx('text-[13px] font-semibold tabular-nums', isDark ? 'text-white' : 'text-slate-900')}>{value}</p>
              <div className={cx('rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em]', selected ? 'bg-[var(--clero-primary)] text-white' : (isDark ? 'bg-white/[0.08] text-slate-300' : 'bg-white text-slate-700'))}>
                Split
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const BankTransferModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div className="absolute inset-0 grid grid-cols-[1.08fr_0.92fr] gap-4 p-4">
    <div className={cx('rounded-[24px] border p-5 shadow-[0_24px_60px_rgba(2,8,23,0.08)]', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-white')}>
      <p className={cx('text-[10px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Auto-transfer</p>
      <p className={cx('mt-2 text-[17px] font-semibold tracking-[-0.03em]', isDark ? 'text-white' : 'text-slate-900')}>Get paid automatically.</p>
      <p className={cx('mt-3 text-[12px] leading-6', isDark ? 'text-slate-300' : 'text-slate-600')}>When someone pays you, Clero processes the payment and deposits it to your bank automatically.</p>

      <div className="mt-6 grid grid-cols-3 gap-3">
        {[
          ['Friend pays', 'Paid by link'],
          ['Money processes', '1-3 business days'],
          ['Deposited', isMotionActive ? 'To Chase •••• 1024' : 'Auto-transfer enabled'],
        ].map(([title, caption], index) => (
          <div className="text-center" key={title}>
            <div className={cx('mx-auto flex h-12 w-12 items-center justify-center rounded-full', index === 0 ? 'bg-emerald-50 text-emerald-600' : index === 1 ? (isDark ? 'bg-white/[0.06] text-slate-300' : 'bg-slate-100 text-slate-500') : 'bg-blue-50 text-blue-600')}>
              {index === 0 ? <CheckCircle2 className="h-5 w-5" /> : index === 1 ? <ArrowRight className="h-4 w-4" /> : <Store className="h-4 w-4" />}
            </div>
            <p className={cx('mt-3 text-[11px] font-semibold leading-5', isDark ? 'text-white' : 'text-slate-900')}>{title}</p>
            <p className={cx('mt-1 text-[10px] leading-4', isDark ? 'text-slate-400' : 'text-slate-500')}>{caption}</p>
          </div>
        ))}
      </div>
    </div>

    <div className="space-y-3">
      <div className={cx('rounded-[20px] border p-4.5', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-[#F8FAFC]')}>
        <p className={cx('text-[10px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Transfer status</p>
        <p className={cx('mt-2 text-[15px] font-semibold tracking-[-0.02em] leading-6', isDark ? 'text-white' : 'text-slate-900')}>
          {isMotionActive ? 'Auto-transfer enabled' : '$184.00 processing'}
        </p>
        <p className={cx('mt-2 text-[12px]', isDark ? 'text-slate-400' : 'text-slate-500')}>
          {isMotionActive ? 'Chase •••• 1024' : 'Usually available in 1-3 business days'}
        </p>
      </div>

      <div className={cx('rounded-[20px] border p-4.5', isDark ? 'border-white/[0.08] bg-white/[0.06]' : 'border-[#E2E8F0] bg-white')}>
        <p className={cx('text-[10px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Receive payments</p>
        <div className="mt-3 space-y-2.5">
          {[
            ['Bank', 'Chase •••• 1024'],
            ['Deposits', 'Go here automatically'],
            ['Setup', 'One-time connection'],
          ].map(([label, value]) => (
            <div className="flex items-start justify-between gap-3" key={label}>
              <span className={cx('text-[12px]', isDark ? 'text-slate-300' : 'text-slate-600')}>{label}</span>
              <span className={cx('text-right text-[12px] font-semibold leading-5', isDark ? 'text-white' : 'text-slate-900')}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const GroupSettlementModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div className="absolute inset-0 grid grid-cols-[1.05fr_0.95fr] gap-4 p-4">
    <div className={cx('rounded-[24px] border p-4 shadow-[0_24px_60px_rgba(2,8,23,0.08)]', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-white')}>
      <p className={cx('text-[10px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Debt simplification</p>
      <p className={cx('mt-2 text-[18px] font-semibold tracking-[-0.03em]', isDark ? 'text-white' : 'text-slate-900')}>Fewer payments. Less back and forth.</p>
      <div className={cx('mt-4 space-y-4 border-t pt-4', isDark ? 'border-white/[0.08]' : 'border-slate-200')}>
        <div>
          <p className={cx('mb-2 text-[10px] font-semibold uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Dinner crew</p>
          <div className="space-y-2">
            {[
              ['Ash → Maya', '$24.10'],
              ['Jules → Maya', isMotionActive ? '$41.29' : '$36.82'],
            ].map(([label, value]) => (
              <div className="flex items-center justify-between" key={label}>
                <span className={cx('text-[13px]', isDark ? 'text-slate-300' : 'text-slate-600')}>{label}</span>
                <span className={cx('text-[13px] font-semibold tabular-nums', isDark ? 'text-white' : 'text-slate-900')}>{value}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <p className={cx('mb-2 text-[10px] font-semibold uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Trip house</p>
          <div className="flex items-center justify-between">
            <span className={cx('text-[13px]', isDark ? 'text-slate-300' : 'text-slate-600')}>Nina → Maya</span>
            <span className={cx('text-[13px] font-semibold tabular-nums', isDark ? 'text-white' : 'text-slate-900')}>$84.55</span>
          </div>
        </div>
      </div>
    </div>

    <div className="space-y-3">
      {[
        ['Trip house', isMotionActive ? '1 payout ready' : '3 open balances'],
        ['Roommates', '2 open balances'],
        ['Event crew', 'Settle monthly'],
      ].map(([name, meta]) => (
        <div className={cx('flex items-center justify-between rounded-[18px] border px-4 py-3', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-white')} key={name}>
          <div>
            <p className={cx('text-[14px] font-semibold tracking-[-0.02em]', isDark ? 'text-white' : 'text-slate-900')}>{name}</p>
            <p className={cx('mt-1 text-[12px]', isDark ? 'text-slate-400' : 'text-slate-500')}>{meta}</p>
          </div>
          <AppAvatarStack
            isDark={isDark}
            people={[
              { label: 'MA', color: '#5EEAD4' },
              { label: 'AS', color: '#7DD3FC' },
              { label: 'NI', color: '#CBD5E1' },
            ]}
          />
        </div>
      ))}
    </div>
  </div>
);

const SettlementPreviewModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div className="absolute inset-0 grid grid-cols-[1.1fr_0.9fr] gap-4 p-4">
    <div className={cx('rounded-[24px] border p-4 shadow-[0_24px_60px_rgba(2,8,23,0.08)]', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-white')}>
      <p className={cx('text-[11px] font-semibold tracking-[-0.01em]', isDark ? 'text-white' : 'text-slate-950')}>Settlement ready</p>
      <p className={cx('mt-1 text-[12px]', isDark ? 'text-slate-400' : 'text-slate-500')}>Review and confirm</p>
      <p className={cx('mt-3 text-[13px] leading-6', isDark ? 'text-slate-300' : 'text-slate-600')}>Fewer payments. Less back and forth.</p>

      <div className={cx('mt-4 space-y-4 border-t pt-4', isDark ? 'border-white/[0.08]' : 'border-slate-200')}>
        <div>
          <p className={cx('mb-2 text-[10px] font-semibold uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>You will pay</p>
          <div className="space-y-2">
            {[
              ['Ash → Maya', '$24.10'],
              ['Jules → Maya', isMotionActive ? '$41.29' : '$36.82'],
            ].map(([label, value]) => (
              <div className="flex items-center justify-between" key={label}>
                <span className={cx('text-[13px]', isDark ? 'text-slate-300' : 'text-slate-600')}>{label}</span>
                <span className={cx('text-[13px] font-semibold tabular-nums', isDark ? 'text-amber-200' : 'text-amber-700')}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <p className={cx('mb-2 text-[10px] font-semibold uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>You will receive</p>
          <div className="flex items-center justify-between">
            <span className={cx('text-[13px]', isDark ? 'text-slate-300' : 'text-slate-600')}>Nina → Maya</span>
            <span className={cx('text-[13px] font-semibold tabular-nums', isDark ? 'text-emerald-200' : 'text-emerald-700')}>{isMotionActive ? '$84.55' : '$35.48'}</span>
          </div>
        </div>

        <div>
          <p className={cx('mb-2 text-[10px] font-semibold uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Other group payments</p>
          <div className="flex items-center justify-between">
            <span className={cx('text-[13px]', isDark ? 'text-slate-300' : 'text-slate-600')}>Roommates balance</span>
            <span className={cx('text-[13px] font-semibold tabular-nums', isDark ? 'text-white' : 'text-slate-900')}>$62.00</span>
          </div>
        </div>
      </div>
    </div>

    <div className="space-y-3">
      <div className={cx('rounded-[20px] p-4', isDark ? 'bg-white/[0.05]' : 'bg-slate-100')}>
        <p className={cx('text-[10px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Participants</p>
        <div className="mt-3 space-y-2">
          {[
            ['Paid', isMotionActive ? '4' : '2'],
            ['Pending', isMotionActive ? '0' : '2'],
            ['Ready', isMotionActive ? '1 payout' : '0 payouts'],
          ].map(([label, value]) => (
            <div className="flex items-center justify-between" key={label}>
              <span className={cx('text-[13px]', isDark ? 'text-slate-300' : 'text-slate-600')}>{label}</span>
              <span className={cx('text-[13px] font-semibold tabular-nums', isDark ? 'text-white' : 'text-slate-900')}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={cx('rounded-[18px] border px-4 py-4', isDark ? 'border-white/[0.08] bg-white/[0.06]' : 'border-[#E2E8F0] bg-white')}>
        <p className={cx('text-[10px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Payout ready</p>
        <p className={cx('mt-2 text-xl font-semibold tracking-[-0.04em] tabular-nums', isDark ? 'text-white' : 'text-slate-950')}>
          {isMotionActive ? '$184.00' : '$96.40'}
        </p>
        <p className={cx('mt-2 text-[12px]', isDark ? 'text-slate-400' : 'text-slate-500')}>
          {isMotionActive ? 'All participant states are resolved.' : 'Waiting on final payer.'}
        </p>
      </div>
    </div>
  </div>
);

const GroupSystemModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div className="grid h-full grid-cols-[1.02fr_0.98fr] gap-4">
    <div className="space-y-3">
      {[
        ['Trip house', '2 open balances'],
        ['Dinner crew', isMotionActive ? '1 payout ready' : '3 open balances'],
        ['Roommates', '2 unclaimed items'],
      ].map(([name, meta]) => (
        <div className={cx('flex items-center justify-between rounded-[18px] border px-4 py-3', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-white')} key={name}>
          <div>
            <p className={cx('text-[14px] font-semibold tracking-[-0.02em]', isDark ? 'text-white' : 'text-slate-900')}>{name}</p>
            <p className={cx('mt-1 text-[12px]', isDark ? 'text-slate-400' : 'text-slate-500')}>{meta}</p>
          </div>
          <AppAvatarStack
            isDark={isDark}
            people={[
              { label: 'MA', color: '#5EEAD4' },
              { label: 'AS', color: '#7DD3FC' },
              { label: 'NI', color: '#CBD5E1' },
            ]}
          />
        </div>
      ))}
    </div>

    <div className="space-y-3">
      <div className={cx('rounded-[20px] border px-4 py-4', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-[#F8FAFC]')}>
        <p className={cx('text-[10px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Latest expense</p>
        <p className={cx('mt-2 text-[16px] font-semibold tracking-[-0.02em]', isDark ? 'text-white' : 'text-slate-900')}>Lake house groceries</p>
        <div className="mt-4 space-y-2">
          {[
            ['You are owed', '$32.10'],
            ['Unclaimed items', isMotionActive ? '0' : '2'],
            ['Settlement', isMotionActive ? 'Ready' : 'Reviewing'],
          ].map(([label, value]) => (
            <div className="flex items-center justify-between" key={label}>
              <span className={cx('text-[13px]', isDark ? 'text-slate-300' : 'text-slate-600')}>{label}</span>
              <span className={cx('text-[13px] font-semibold tabular-nums', isDark ? 'text-white' : 'text-slate-900')}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className={cx('rounded-[18px] border px-4 py-4', isDark ? 'border-white/[0.08] bg-white/[0.06]' : 'border-[#E2E8F0] bg-white')}>
        <p className={cx('text-[10px] font-medium uppercase tracking-[0.22em]', isDark ? 'text-slate-400' : 'text-slate-500')}>Shared history</p>
        <div className="mt-3 space-y-2">
          {[
            ['Dinner on Main', '$79.68'],
            ['Cab to airport', '$42.00'],
            ['Groceries', '$126.54'],
          ].map(([name, value]) => (
            <div className="flex items-center justify-between" key={name}>
              <span className={cx('text-[13px]', isDark ? 'text-slate-300' : 'text-slate-600')}>{name}</span>
              <span className={cx('text-[13px] font-semibold tabular-nums', isDark ? 'text-white' : 'text-slate-900')}>{value}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const UtilityRailModule: React.FC<{ isDark: boolean; isMotionActive?: boolean }> = ({ isDark, isMotionActive = false }) => (
  <div className="grid h-full grid-cols-[0.28fr_1.15fr_0.78fr] gap-3">
    <div className={cx('rounded-[18px] border', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-[#F8FAFC]')} />
    <div className="space-y-3">
      <div className={cx('rounded-[12px] border px-3 py-2', isDark ? 'border-white/[0.08] bg-white/[0.06]' : 'border-[#E2E8F0] bg-white')}>
        <div className="flex items-center justify-between">
          <span className={cx('text-[11px] font-medium', isDark ? 'text-slate-300' : 'text-slate-600')}>Checkout capture</span>
          <span className={cx('text-[10px] font-semibold uppercase tracking-[0.22em]', isDark ? 'text-teal-200' : 'text-teal-700')}>{isMotionActive ? 'Ready' : 'Captured'}</span>
        </div>
      </div>
      <div className={cx('rounded-[12px] px-3 py-2', isDark ? 'bg-white/[0.05]' : 'bg-slate-100')}>
        <p className={cx('text-[11px]', isDark ? 'text-slate-300' : 'text-slate-600')}>
          {isMotionActive ? 'purchase details ready to split' : 'details captured from checkout'}
        </p>
      </div>
    </div>
    <div className="space-y-3">
      <div className={cx('rounded-[10px] border px-3 py-2', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-white')}>
        <p className={cx('text-[11px] font-medium', isDark ? 'text-slate-300' : 'text-slate-600')}>Dinner on Main</p>
      </div>
      <div className={cx('rounded-[14px] border px-3 py-3', isDark ? 'border-white/[0.08] bg-white/[0.05]' : 'border-[#E2E8F0] bg-white')}>
        <p className={cx('text-[11px]', isDark ? 'text-slate-300' : 'text-slate-600')}>$79.68 total</p>
      </div>
    </div>
  </div>
);

const ScreenshotPlaceholderSurface: React.FC<{
  isDark: boolean;
  title: string;
  subtitle: string;
  slotLabel?: string;
  compact?: boolean;
}> = ({ compact = false, isDark, slotLabel, subtitle, title }) => (
  <div className="absolute inset-0 p-4">
    <div
      className={cx(
        'relative flex h-full flex-col items-center justify-center overflow-hidden rounded-[24px] border px-5 text-center',
        compact ? 'gap-3 py-6' : 'gap-4 py-8',
        isDark
          ? 'border-white/[0.08] bg-[linear-gradient(180deg,rgba(255,255,255,0.05)_0%,rgba(255,255,255,0.02)_100%)]'
          : 'border-[#D7DFEA] bg-[linear-gradient(180deg,rgba(255,255,255,0.96)_0%,rgba(248,250,252,0.96)_100%)]'
      )}
    >
      <div
        className={cx(
          'absolute inset-5 rounded-[20px] border border-dashed',
          isDark ? 'border-white/[0.1]' : 'border-slate-300/90'
        )}
      />
      <div
        className={cx(
          'absolute inset-x-10 top-8 h-20 rounded-full blur-3xl',
          isDark ? 'bg-teal-400/10' : 'bg-teal-100/90'
        )}
      />
      {slotLabel ? (
        <span
          className={cx(
            'relative inline-flex items-center rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em]',
            isDark ? 'bg-white/[0.06] text-slate-300' : 'bg-slate-100 text-slate-500'
          )}
        >
          {slotLabel}
        </span>
      ) : null}
      <div className="relative space-y-2">
        <p className={cx(compact ? 'text-[18px]' : 'text-[22px]', 'font-semibold tracking-[-0.04em]', isDark ? 'text-white' : 'text-slate-900')}>
          {title}
        </p>
        <p className={cx(compact ? 'max-w-[250px] text-[12px] leading-5' : 'max-w-[360px] text-[13px] leading-6', isDark ? 'text-slate-400' : 'text-slate-500')}>
          {subtitle}
        </p>
      </div>
    </div>
  </div>
);

export const LaunchDeviceFrame: React.FC<{
  title: string;
  subtitle: string;
  slotLabel?: string;
  tone?: ProductSurfaceTone;
  className?: string;
  layoutVariant?: 'default' | 'claim' | 'dashboard';
  contentMode?: FrameContentMode;
  screenshotSrc?: string;
  screenshotAlt?: string;
  screenshotCropTopPercent?: number;
  screenshotIncludesDeviceFrame?: boolean;
  motionState?: ProductFrameMotionState;
  isMotionActive?: boolean;
  motionStage?: 'two-paid' | 'three-paid' | 'four-paid' | 'settled';
}> = ({
  className,
  contentMode = 'mock',
  isMotionActive = false,
  layoutVariant = 'default',
  motionStage,
  motionState,
  screenshotAlt,
  screenshotCropTopPercent = 0,
  screenshotIncludesDeviceFrame = false,
  screenshotSrc,
  slotLabel,
  subtitle,
  tone = 'dark',
  title,
}) => {
  const isDark = tone === 'dark';
  const isDashboard = layoutVariant === 'dashboard' || layoutVariant === 'default';
  const isClaim = layoutVariant === 'claim';
  const showPlaceholder = contentMode === 'placeholder';
  const showScreenshot = showPlaceholder && Boolean(screenshotSrc);

  if (showScreenshot && screenshotIncludesDeviceFrame) {
    return (
      <div className={cx('mx-auto w-full max-w-[392px] [transform-style:preserve-3d]', className)}>
        <img
          alt={screenshotAlt || title}
          className="block h-auto w-full"
          src={screenshotSrc}
        />
      </div>
    );
  }

  return (
    <div className={cx('mx-auto w-full max-w-[292px] [transform-style:preserve-3d]', className)}>
      <div
        className={cx(
          'relative rounded-[44px] p-[9px] shadow-[0_48px_120px_rgba(2,8,23,0.28)] before:pointer-events-none before:absolute before:inset-[1px] before:rounded-[43px] before:border before:border-white/[0.12] before:content-[""]',
          isDark
            ? 'bg-[linear-gradient(165deg,#0a1119_0%,#121d29_55%,#0a121a_100%)]'
            : 'bg-[linear-gradient(165deg,#dce0e4_0%,#f5f5f1_45%,#cfd5da_100%)]'
        )}
      >
        <div
          className={cx(
            'absolute inset-x-8 top-[10px] h-14 rounded-full blur-2xl',
            isDark ? 'bg-white/[0.07]' : 'bg-white/80'
          )}
        />
        <div
          className={cx(
            'relative overflow-hidden rounded-[35px] border p-3',
            isDark ? 'border-white/[0.06] bg-[#0b131d]' : 'border-white/80 bg-[#eef0ec]'
          )}
        >
          {!showScreenshot ? (
            <div className="flex justify-center pb-3">
              <div className={cx('h-1.5 w-20 rounded-full', isDark ? 'bg-white/[0.12]' : 'bg-slate-300/90')} />
            </div>
          ) : null}
          <div
            className={cx(
              'relative flex flex-col overflow-hidden rounded-[27px]',
              isClaim ? 'aspect-[9/20.8]' : 'aspect-[9/19.5]',
              showScreenshot
                ? isDark
                  ? 'border border-white/[0.06] bg-[#0c1520]'
                  : 'bg-[linear-gradient(180deg,#F7F7F4_0%,#EFF1EC_100%)]'
                : isDark
                  ? 'border border-white/[0.06] px-4 pb-4 pt-5 bg-[radial-gradient(circle_at_50%_8%,rgba(255,255,255,0.12),transparent_24%),radial-gradient(circle_at_18%_18%,rgba(24,183,174,0.18),transparent_26%),linear-gradient(180deg,#101a26_0%,#0c1520_100%)]'
                  : isDashboard
                    ? 'px-4 pb-5 pt-5 bg-[linear-gradient(180deg,#F7F7F4_0%,#EFF1EC_100%)]'
                    : 'border border-white px-4 pb-4 pt-5 bg-[radial-gradient(circle_at_50%_8%,rgba(255,255,255,0.85),transparent_26%),radial-gradient(circle_at_18%_18%,rgba(15,23,42,0.06),transparent_24%),linear-gradient(180deg,#ffffff_0%,#edf0ea_100%)]'
            )}
          >
            {!showScreenshot ? (
              <div
                className={cx(
                  'pointer-events-none absolute inset-0',
                  isDark
                    ? 'bg-[linear-gradient(180deg,rgba(255,255,255,0.16)_0%,rgba(255,255,255,0.02)_18%,rgba(255,255,255,0)_42%,rgba(255,255,255,0.04)_100%)]'
                    : isDashboard
                      ? 'bg-[linear-gradient(180deg,rgba(255,255,255,0.28)_0%,rgba(255,255,255,0)_18%,rgba(15,23,42,0.03)_100%)]'
                      : 'bg-[linear-gradient(180deg,rgba(255,255,255,0.75)_0%,rgba(255,255,255,0.08)_20%,rgba(255,255,255,0)_44%,rgba(15,23,42,0.04)_100%)]'
                )}
              />
            ) : null}
            <div className="relative flex-1">
              {showPlaceholder ? (
                screenshotSrc ? (
                  <div className="absolute inset-0 overflow-hidden rounded-[27px] bg-white/[0.02]">
                    <img
                      alt={screenshotAlt || title}
                      className="absolute left-0 top-0 w-full max-w-none"
                      style={{
                        transform: `translateY(-${screenshotCropTopPercent}%)`,
                      }}
                      src={screenshotSrc}
                    />
                  </div>
                ) : (
                  <ScreenshotPlaceholderSurface compact isDark={isDark} slotLabel={slotLabel} subtitle={subtitle} title={title} />
                )
              ) : null}
              {!showPlaceholder && layoutVariant === 'dashboard' ? <OrganizerHeroModule embedded isDark={isDark} isMotionActive={isMotionActive} motionStage={motionStage} /> : null}
              {!showPlaceholder && layoutVariant === 'claim' ? <ClaimDeviceModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
              {!showPlaceholder && layoutVariant === 'default' ? <OrganizerHeroModule embedded isDark={isDark} isMotionActive={isMotionActive} motionStage={motionStage} /> : null}
              {!showPlaceholder && motionState ? <FrameStatePill isDark={isDark} isMotionActive={isMotionActive} state={motionState} /> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const EditorialCropFrame: React.FC<{
  title: string;
  subtitle: string;
  slotLabel?: string;
  tone?: ProductSurfaceTone;
  className?: string;
  layoutVariant?: EditorialLayoutVariant;
  contentMode?: FrameContentMode;
  screenshotSrc?: string;
  screenshotAlt?: string;
  screenshotAspectRatio?: number;
  screenshotFit?: 'cover' | 'contain';
  screenshotIncludesDeviceFrame?: boolean;
  motionState?: ProductFrameMotionState;
  isMotionActive?: boolean;
}> = ({
  className,
  contentMode = 'mock',
  isMotionActive = false,
  layoutVariant = 'default',
  motionState,
  screenshotAlt,
  screenshotAspectRatio,
  screenshotFit = 'cover',
  screenshotIncludesDeviceFrame = false,
  screenshotSrc,
  slotLabel,
  subtitle,
  tone = 'light',
  title,
}) => {
  const isDark = tone === 'dark';
  const showPlaceholder = contentMode === 'placeholder';
  const showScreenshot = showPlaceholder && Boolean(screenshotSrc);
  const aspectClassName = {
    default: 'aspect-[16/10]',
    'receipt-scan': 'aspect-[16/10]',
    'hero-receipt': 'aspect-[16/9.6]',
    'claim-stack': 'aspect-[16/10]',
    'claim-slider': 'aspect-[16/13.2]',
    'payer-card': 'aspect-[16/10]',
    'hero-payer': 'aspect-[16/8.8]',
    'settlement-preview': 'aspect-[16/10]',
    'group-system': 'aspect-[16/11]',
    'utility-rail': 'aspect-[16/6]',
    'purchase-summary': 'aspect-[16/10]',
    'wallet-transactions': 'aspect-[16/10]',
    'bank-transfer': 'aspect-[16/9]',
    'group-settlement': 'aspect-[16/10]',
  }[layoutVariant];

  if (showScreenshot && screenshotIncludesDeviceFrame) {
    return (
      <div className={cx('mx-auto w-full max-w-[392px]', className)}>
        <img
          alt={screenshotAlt || title}
          className="block h-auto w-full"
          src={screenshotSrc}
        />
      </div>
    );
  }

  return (
    <div
      className={cx(
        'relative overflow-hidden border shadow-[0_32px_90px_rgba(2,8,23,0.14)]',
        isDark
          ? 'rounded-[32px] border-white/[0.08] bg-[radial-gradient(circle_at_18%_20%,rgba(24,183,174,0.16),transparent_24%),radial-gradient(circle_at_82%_16%,rgba(98,199,255,0.09),transparent_20%),linear-gradient(160deg,#09121b_0%,#0f1822_100%)] text-white'
          : 'rounded-[28px] border-slate-200/80 bg-[radial-gradient(circle_at_top,rgba(15,23,42,0.06),transparent_22%),linear-gradient(180deg,#ffffff_0%,#f0f2ed_100%)] text-slate-950'
      )}
    >
      <div
        className={cx(
          'pointer-events-none absolute inset-0',
          isDark
            ? 'bg-[linear-gradient(180deg,rgba(255,255,255,0.08)_0%,rgba(255,255,255,0)_26%,rgba(255,255,255,0.02)_100%)]'
            : 'bg-[linear-gradient(180deg,rgba(255,255,255,0.7)_0%,rgba(255,255,255,0)_24%,rgba(15,23,42,0.03)_100%)]'
        )}
      />
      <div className="relative p-4 sm:p-5">
        <div
          className={cx(
            'relative overflow-hidden rounded-[24px] border p-4',
            aspectClassName,
            showScreenshot
              ? isDark
                ? 'border-white/[0.08] bg-white/[0.03] p-0'
                : 'border-white/80 bg-white/80 p-0'
              : isDark
                ? 'border-white/[0.08] bg-white/[0.03]'
                : 'border-white/80 bg-white/80'
          )}
          style={showScreenshot && screenshotAspectRatio ? { aspectRatio: String(screenshotAspectRatio) } : undefined}
        >
          {showPlaceholder ? (
            showScreenshot ? (
              <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-4">
                <img
                  alt={screenshotAlt || title}
                  className={cx(
                    'max-h-full max-w-full rounded-[20px] border shadow-[0_18px_44px_rgba(15,23,42,0.12)]',
                    isDark ? 'border-white/10 bg-white' : 'border-slate-200/90 bg-white',
                    screenshotFit === 'contain' ? 'object-contain' : 'h-full w-full object-cover',
                    screenshotFit === 'contain' ? 'object-top' : 'object-center'
                  )}
                  src={screenshotSrc}
                />
              </div>
            ) : (
              <ScreenshotPlaceholderSurface isDark={isDark} slotLabel={slotLabel} subtitle={subtitle} title={title} />
            )
          ) : null}
          {!showPlaceholder && layoutVariant === 'receipt-scan' ? <ReceiptAnalysisModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
          {!showPlaceholder && layoutVariant === 'hero-receipt' ? <HeroReceiptModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
          {!showPlaceholder && layoutVariant === 'claim-stack' ? <ClaimStackModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
          {!showPlaceholder && layoutVariant === 'claim-slider' ? <ClaimSliderModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
          {!showPlaceholder && layoutVariant === 'payer-card' ? <div className="absolute inset-0 p-4"><PayerHeroModule isDark={isDark} isMotionActive={isMotionActive} /></div> : null}
          {!showPlaceholder && layoutVariant === 'hero-payer' ? <div className="absolute inset-0 p-4"><HeroPayerModule isDark={isDark} isMotionActive={isMotionActive} /></div> : null}
          {!showPlaceholder && layoutVariant === 'group-system' ? <GroupSystemModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
          {!showPlaceholder && layoutVariant === 'settlement-preview' ? <SettlementPreviewModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
          {!showPlaceholder && layoutVariant === 'utility-rail' ? <UtilityRailModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
          {!showPlaceholder && layoutVariant === 'purchase-summary' ? <PurchaseSummaryModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
          {!showPlaceholder && layoutVariant === 'wallet-transactions' ? <WalletTransactionsModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
          {!showPlaceholder && layoutVariant === 'bank-transfer' ? <BankTransferModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
          {!showPlaceholder && layoutVariant === 'group-settlement' ? <GroupSettlementModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
          {!showPlaceholder && layoutVariant === 'default' ? <SettlementPreviewModule isDark={isDark} isMotionActive={isMotionActive} /> : null}
          {!showPlaceholder && motionState ? <FrameStatePill isDark={isDark} isMotionActive={isMotionActive} state={motionState} /> : null}
        </div>
      </div>
    </div>
  );
};

export const PhoneScreenshotPlaceholder: React.FC<{
  title: string;
  subtitle: string;
  tone?: ProductSurfaceTone;
  className?: string;
}> = (props) => <LaunchDeviceFrame {...props} />;

export const CropScreenshotPlaceholder: React.FC<{
  title: string;
  subtitle: string;
  tone?: ProductSurfaceTone;
  className?: string;
  slotLabel?: string;
}> = (props) => <EditorialCropFrame {...props} contentMode="placeholder" />;

export const StatCard: React.FC<{ label: string; value: string; detail: string; className?: string }> = ({
  className,
  detail,
  label,
  value,
}) => (
  <SurfaceCard className={cx('p-5', className)}>
    <p className="text-[11px] font-black uppercase tracking-[0.22em] text-slate-500">{label}</p>
    <p className="mt-3 text-3xl font-bold tracking-[-0.03em] text-slate-950">{value}</p>
    <p className="mt-2 text-sm leading-6 text-slate-600">{detail}</p>
  </SurfaceCard>
);

export const SectionHeader: React.FC<{
  eyebrow: string;
  title: string;
  description: string;
  align?: 'left' | 'center';
  className?: string;
}> = ({ align = 'left', className, description, eyebrow, title }) => (
  <div className={cx(align === 'center' ? 'mx-auto max-w-3xl text-center' : 'max-w-2xl', className)}>
    <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-slate-500">{eyebrow}</p>
    <h2 className="mt-4 text-3xl font-bold tracking-[-0.04em] text-slate-950 sm:text-4xl md:text-5xl">{title}</h2>
    <p className="mt-4 text-base leading-7 text-slate-600 sm:text-lg">{description}</p>
  </div>
);

export type TopBarNavItem = {
  label: string;
  onClick: () => void;
};

type TopBarProps = {
  navItems: TopBarNavItem[];
  actions?: ReactNode;
};

export const TopBar: React.FC<TopBarProps> = ({ actions, navItems }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 768) setIsMenuOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsMenuOpen(false);
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const handleNavClick = (onClick: () => void) => {
    setIsMenuOpen(false);
    onClick();
  };

  return (
    <header
      className={cx(
        'sticky left-0 right-0 z-50 w-full border-b px-3 sm:px-5',
        isScrolled
          ? 'top-0 border-slate-200/90 bg-[rgba(255,255,255,0.94)] shadow-[0_12px_28px_rgba(15,23,42,0.06)] backdrop-blur-[22px]'
          : 'top-0 border-transparent bg-[rgba(255,255,255,0.78)] backdrop-blur-[18px]'
      )}
    >
      <div
        className={cx(
          'mx-auto w-full max-w-[var(--clero-max-content)] px-0 py-2.5 transition duration-300 sm:px-0 lg:px-0',
          isScrolled ? 'py-2.5' : 'py-3'
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <button
            className="inline-flex items-center rounded-full px-1 py-1 transition hover:bg-white/75"
            onClick={() => {
              setIsMenuOpen(false);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            type="button"
          >
            <Logo className="justify-start -ml-1 scale-[1.5] origin-left" size="xs" />
          </button>

          <nav className="hidden items-center gap-1 lg:flex">
            {navItems.map((item) => (
              <button
                className="rounded-full px-3 py-2 text-sm font-medium text-slate-600 transition hover:text-slate-950"
                key={item.label}
                onClick={() => handleNavClick(item.onClick)}
                type="button"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex shrink-0 items-center gap-2">
            <div className="flex items-center">{actions}</div>
            <button
              aria-expanded={isMenuOpen}
              aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200/90 bg-white/84 text-slate-700 transition hover:border-slate-300 hover:bg-white hover:text-slate-950 lg:hidden"
              onClick={() => setIsMenuOpen((current) => !current)}
              type="button"
            >
              {isMenuOpen ? <X className="h-4.5 w-4.5" /> : <Menu className="h-4.5 w-4.5" />}
            </button>
          </div>
        </div>

        {isMenuOpen ? (
          <div className="mt-3 border-t border-slate-200/80 pb-1 pt-3 lg:hidden">
            <nav className="grid gap-2">
              {navItems.map((item) => (
                <button
                  className="flex items-center justify-between rounded-[18px] bg-white/82 px-4 py-3 text-left text-[15px] font-medium text-slate-700 transition hover:bg-white hover:text-slate-950"
                  key={item.label}
                  onClick={() => handleNavClick(item.onClick)}
                  type="button"
                >
                  <span>{item.label}</span>
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </button>
              ))}
            </nav>
          </div>
        ) : null}
      </div>
    </header>
  );
};

export const PayerShortcut: React.FC<{ onClick: () => void; className?: string }> = ({ className, onClick }) => (
  <GhostButton className={cx('w-full justify-between px-4 py-4 sm:w-auto', className)} onClick={onClick}>
    <span className="inline-flex items-center gap-3">
      <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-950/6 text-slate-700">
        <Search className="h-5 w-5" />
      </span>
      <span className="text-left leading-tight">
        <span className="block text-[10px] font-black uppercase tracking-[0.22em] text-slate-500">Already invited?</span>
        <span className="block text-base text-slate-900">Find My Clero</span>
      </span>
    </span>
    <ArrowRight className="h-4 w-4 text-slate-500" />
  </GhostButton>
);

export const IPhoneShowcaseDevice: React.FC<{
  src: string;
  alt: string;
  className?: string;
}> = ({ src, alt, className = '' }) => {
  return (
    <div
      className={`relative mx-auto w-full select-none rounded-[32px] sm:rounded-[44px] bg-slate-950 shadow-[0_12px_28px_rgba(2,8,23,0.12)] sm:shadow-[0_20px_45px_rgba(2,8,23,0.16)] ${className}`}
      style={{ aspectRatio: '1022 / 2082' }}
    >
      {/* Authentic iPhone Bezel Base Frame */}
      <picture>
        <source srcSet="/mockup.avif" type="image/avif" />
        <source srcSet="/mockup.webp" type="image/webp" />
        <img
          alt="iPhone Shell Frame"
          className="pointer-events-none block h-full w-full object-contain"
          draggable={false}
          loading="lazy"
          src="/mockup.png"
        />
      </picture>

      {/* Inner Screen Display (z-10 on top of mockup) */}
      <div
        className="absolute z-10 overflow-hidden bg-white"
        style={{
          left: '5.088%',
          top: '2.209%',
          width: '89.824%',
          height: '95.581%',
          borderRadius: '13.725% / 6.332%',
        }}
      >
        <img
          alt={alt}
          className="absolute inset-0 block h-full w-full object-cover object-top pointer-events-none"
          draggable={false}
          loading="lazy"
          src={src}
        />
      </div>
    </div>
  );
};

export const BottomAppShowcase: React.FC<{
  children?: ReactNode;
  className?: string;
  id?: string;
}> = memo(({ children, className = '', id = 'bottom-download' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold: 0.15 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      id={id}
      ref={containerRef}
      className={`overflow-hidden rounded-[28px] sm:rounded-[44px] bg-gradient-to-b from-[#F7FAF9] via-[#FAFDFB] to-[#F2F6F5] px-4 py-10 sm:p-12 lg:p-16 border border-slate-200/50 ${className}`}
    >
      {/* Top Visual: Multi-iPhone Fan with Scroll-Triggered Fan-out Animation */}
      <div className="mx-auto max-w-6xl px-1 sm:px-4">
        <div className="relative flex items-center justify-center -space-x-7 xs:-space-x-9 sm:-space-x-16 md:-space-x-20 lg:-space-x-24 pt-4 pb-8 sm:pb-12 overflow-visible">
          
          {/* Phone 1: Connected Cards (Far Left) */}
          <div
            className={`hidden md:block w-[160px] lg:w-[180px] xl:w-[205px] shrink-0 transition-all duration-1000 ease-out z-10 ${
              isInView
                ? '-rotate-6 -translate-x-4 translate-y-8'
                : 'rotate-0 translate-x-12 translate-y-0'
            }`}
          >
            <IPhoneShowcaseDevice
              src="/screenshots/accounts_cards_screen.png"
              alt="Connected Cards & Plaid Sync Screen"
            />
          </div>

          {/* Phone 2: Roommates Monthly (Mid Left - visible on mobile & up) */}
          <div
            className={`w-[105px] xs:w-[120px] sm:w-[190px] xl:w-[220px] shrink-0 transition-all duration-1000 ease-out z-20 ${
              isInView
                ? '-rotate-6 sm:-rotate-3 -translate-x-1 sm:-translate-x-2 translate-y-2 sm:translate-y-3'
                : 'rotate-0 translate-x-4 sm:translate-x-6 translate-y-0'
            }`}
          >
            <IPhoneShowcaseDevice
              src="/screenshots/groups_monthly_screen.png"
              alt="Bushwick Roommates Monthly Screen"
            />
          </div>

          {/* Phone 3: Direct Bank Pay (Center Hero) */}
          <div
            className={`w-[122px] xs:w-[140px] sm:w-[225px] xl:w-[245px] shrink-0 rotate-0 translate-y-0 z-30 drop-shadow-2xl transition-all duration-1000 ease-out ${
              isInView ? 'scale-100' : 'scale-95'
            }`}
          >
            <IPhoneShowcaseDevice
              src="/screenshots/direct_pay_screen.png"
              alt="Direct Bank P2P Payment Screen"
            />
          </div>

          {/* Phone 4: Vacation Group (Mid Right - visible on mobile & up) */}
          <div
            className={`w-[105px] xs:w-[120px] sm:w-[190px] xl:w-[220px] shrink-0 transition-all duration-1000 ease-out z-20 ${
              isInView
                ? 'rotate-6 sm:rotate-3 translate-x-1 sm:translate-x-2 translate-y-2 sm:translate-y-3'
                : 'rotate-0 -translate-x-4 sm:-translate-x-6 translate-y-0'
            }`}
          >
            <IPhoneShowcaseDevice
              src="/screenshots/groups_trip_screen.png"
              alt="Lake Tahoe Vacation Group Settlement Screen"
            />
          </div>

          {/* Phone 5: Direct Bank Settlement Activity (Far Right) */}
          <div
            className={`hidden md:block w-[160px] lg:w-[180px] xl:w-[205px] shrink-0 transition-all duration-1000 ease-out z-10 ${
              isInView
                ? 'rotate-6 translate-x-4 translate-y-8'
                : 'rotate-0 -translate-x-12 translate-y-0'
            }`}
          >
            <IPhoneShowcaseDevice
              src="/screenshots/transactions_screen.png"
              alt="Direct Bank Settlement Activity Feed Screen"
            />
          </div>

        </div>
      </div>

      {/* Typography & Download Buttons */}
      <div className="mx-auto max-w-3xl text-center space-y-3 sm:space-y-4 pt-6 pb-4 sm:pt-14 sm:pb-10 px-2">
        <h2 className="text-2xl font-black tracking-tight text-slate-950 sm:text-5xl sm:leading-[1.1]">
          <span>Built for Real Life</span>
          <br />
          <span className="text-[var(--clero-primary)]">Trusted Between Friends</span>
        </h2>

        {/* Official App Store & Google Play Badges */}
        <AppStoreBadges badgeHeightClass="h-10 sm:h-11" className="pt-2 pb-1" />

        <p className="mx-auto max-w-xl text-xs sm:text-base md:text-lg font-medium text-slate-600 leading-relaxed px-2">
          From roommate rent and grocery runs to group trips and weekend dinners, see how people use Clero to pay and settle with zero debt friction.
        </p>
      </div>

      {/* Optional slot for children (e.g. Reviews Marquee on Landing Page) */}
      {children}
    </div>
  );
});

